import React, { useState } from "react";
import { motion } from "framer-motion";
import { useSnackbar } from "notistack";
import useTheme from "../../hooks/useTheme";

// components
import { html } from "./ReactEmail";
import EmailHTML from "./EmailHTML";

const buttonVariants = {
	tap: {
		scale: 0.9,
	},
};

const FormSection = () => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar(0);
	const { theme } = useTheme();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [contact, setContact] = useState("");
	const [subject, setSubject] = useState("");
	const [messageBody, setMessageBody] = useState("");
	const [hasErrors, setHasErrors] = useState([]);
	const [responseMessage, setResponseMessage] = useState("");

	const validate = async () => {
		const errors = [];

		if (!name) {
			errors.push("Full name is required");
			setHasErrors((prev) => [...prev, "name"]);
		}

		if (!email) {
			errors.push("Email is required");
			setHasErrors((prev) => [...prev, "email"]);
		}

		if (!contact) {
			errors.push("Contact # is required");
			setHasErrors((prev) => [...prev, "contact"]);
		}

		if (!subject) {
			errors.push("Subject is required");
			setHasErrors((prev) => [...prev, "subject"]);
		}

		if (!messageBody) {
			errors.push("Message Body is required");
			setHasErrors((prev) => [...prev, "messageBody"]);
		}

		return errors;
	};

	const handleSendEmail = async (e) => {
		e.preventDefault();

		setHasErrors((prev) => []);

		const errors = await validate();

		if (errors?.length) {
			for (let i = 0; i < errors.length; i++) {
				enqueueSnackbar(errors[i], {
					style: {
						backgroundColor: "red",
						color: "white",
					},
					anchorOrigin: {
						horizontal: "left",
						vertical: "bottom",
					},
				});
			}

			return;
		}

		const body = {
			name,
			contact,
			email,
			subject,
			messageBody: EmailHTML({ name, contact, email, subject, messageBody }),
		};

		try {
			const response = await fetch(
				`https://portfolio-backend-714z.onrender.com/api/mail/send_mail`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(body),
				}
			);

			if (response.ok) {
				const data = await response.json();
				enqueueSnackbar("Email Sent", {
					style: {
						backgroundColor: "green",
						color: "white",
					},
					anchorOrigin: {
						horizontal: "left",
						vertical: "bottom",
					},
				});
			}
		} catch (err) {
			enqueueSnackbar(err, {
				style: {
					backgroundColor: "red",
					color: "white",
				},
				anchorOrigin: {
					horizontal: "left",
					vertical: "bottom",
				},
			});
		}

		setName("");
		setContact("");
		setEmail("");
		setSubject("");
		setMessageBody("");
	};

	return (
		<div className="flex flex-col p-5">
			<h1 className="text-3xl text-center mb-10">
				Let's make something awesome together!
			</h1>
			<div
				className={`p-5 flex flex-col gap-5 justify-center ${
					theme === "dark" ? "bg-white text-black" : ""
				} rounded-2xl border-2 shadow-xl`}>
				<input
					type="text"
					autoFocus
					className={` border focus:outline-none text-md p-2 rounded-lg w-full ${
						hasErrors.includes("subject") ? "border-red-500" : "border-gray-300"
					}`}
					id="subject"
					placeholder="Subject"
					value={subject}
					required
					onChange={(e) => setSubject(e.target.value)}
				/>

				<textarea
					name="messageBody"
					id="messageBody"
					className={`min-h-24 border focus:outline-none text-md p-2 rounded-lg w-full ${
						hasErrors.includes("messageBody")
							? "border-red-500"
							: "border-gray-300"
					}`}
					placeholder="Message"
					value={messageBody}
					required
					onChange={(e) => setMessageBody(e.target.value)}
				/>
				<div className="flex flex-col lg:flex-row items-center gap-5">
					<div className="flex md:gap-2 items-center w-full">
						<label
							className="text-sm lg:text-xl text-green-800 min-w-[60px]"
							htmlFor="name">
							Name:
						</label>
						<input
							type="text"
							className={` border focus:outline-none text-md p-2 rounded-lg grow ${
								hasErrors.includes("name")
									? "border-red-500"
									: "border-gray-300"
							}`}
							id="name"
							value={name}
							required
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
					<div className="flex md:gap-2 items-center w-full">
						<label
							className="text-sm lg:text-xl text-green-800 min-w-[60px]"
							htmlFor="email">
							Email:
						</label>
						<input
							type="email"
							className={` border focus:outline-none text-md p-2 rounded-lg grow ${
								hasErrors.includes("email")
									? "border-red-500"
									: "border-gray-300"
							}`}
							id="email"
							value={email}
							required
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>
					<div className="flex md:gap-2 items-center justify-start w-full">
						<label
							className="text-sm lg:text-xl text-green-800 min-w-[60px]"
							htmlFor="contact">
							Contact:
						</label>
						<input
							type="text"
							className={`border focus:outline-none text-md p-2 rounded-lg grow ${
								hasErrors.includes("contact")
									? "border-red-500"
									: "border-gray-300"
							}`}
							id="contact"
							value={contact}
							required
							onChange={(e) => setContact(e.target.value)}
						/>
					</div>
				</div>

				<div className="min-h-8">
					<span className="text-xs text-gray-500 italic font-semibold">
						*Email messages may show on your inbox or spam.
					</span>
				</div>
				<div className="flex justify-end p-2">
					<motion.button
						variants={buttonVariants}
						whileTap="tap"
						onClick={(e) => handleSendEmail(e)}
						className="h-12 w-32 bg-green-800 hover:bg-green-600 text-white rounded-2xl text-xl font-bold">
						Send
					</motion.button>
				</div>
			</div>
		</div>
	);
};

export default FormSection;
